/**
 * FamilyTreeErrorBoundary — catches any uncaught React render error
 * inside the FamilyTree page so a single bad member node, a NaN
 * coordinate, or a malformed string lookup can never white-screen the
 * recipient's scroll.
 *
 * Owen Feb 2026 round 36 — built after a dummy account that accepted
 * a share invite froze on a blank cream page (a `null.charAt(0)` on a
 * mirrored member with no name string crashed the render and there was
 * no fallback). With this boundary in place, the worst-case UX is "your
 * scroll is loading — try refreshing", not a frozen browser tab.
 */
import { Component } from 'react';
import { Button } from '@/components/ui/button';
import { AlertTriangle, RefreshCw } from 'lucide-react';

class FamilyTreeErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    // Best-effort console signal so the bug shows up in Owen's logs.
    // eslint-disable-next-line no-console
    console.error('FamilyTree render crashed:', error, info);
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: null });
  };

  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    const msg = this.state.error?.message || 'Unexpected error rendering the scroll.';
    return (
      <div
        className="min-h-screen flex items-center justify-center px-4 py-12"
        style={{ background: 'linear-gradient(180deg, #1F1B19 0%, #2A2522 100%)', color: '#F4EFE6' }}
        data-testid="family-tree-error"
      >
        <div className="max-w-md w-full text-center">
          <div className="mx-auto w-14 h-14 rounded-full flex items-center justify-center mb-6" style={{ background: 'rgba(212,175,55,0.15)', border: '1px solid rgba(212,175,55,0.4)' }}>
            <AlertTriangle className="w-6 h-6" style={{ color: '#D4AF37' }} />
          </div>
          <h1
            className="font-light mb-4 leading-tight"
            style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: 'clamp(1.75rem, 4vw, 2.25rem)', color: '#F4EFE6' }}
          >
            Your scroll is still loading
          </h1>
          <p
            className="text-base italic mb-2 leading-relaxed"
            style={{ fontFamily: 'Cormorant Garamond, serif', color: '#C9B99A' }}
          >
            Something hiccupped while we were laying out your family. A refresh usually settles it.
          </p>
          <p
            className="text-xs italic mb-8 opacity-70 break-all leading-relaxed"
            style={{ fontFamily: 'Work Sans, sans-serif', color: '#8A817C' }}
            data-testid="family-tree-error-detail"
          >
            {msg}
          </p>
          <div className="flex flex-wrap justify-center gap-3">
            <Button
              onClick={this.handleReload}
              data-testid="family-tree-error-reload"
              className="px-6 py-4 rounded-full"
              style={{ background: '#D4AF37', color: '#1F1B19', fontFamily: 'Cormorant Garamond, serif', fontStyle: 'italic' }}
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh the page
            </Button>
            <Button
              onClick={this.handleRetry}
              variant="outline"
              data-testid="family-tree-error-retry"
              className="px-6 py-4 rounded-full border-[#D4AF37]/40 text-[#D4AF37]"
              style={{ fontFamily: 'Cormorant Garamond, serif', fontStyle: 'italic' }}
            >
              Try again
            </Button>
          </div>
        </div>
      </div>
    );
  }
}

export default FamilyTreeErrorBoundary;
